import { keccak256 } from '@ethersproject/keccak256'
import { MAX_WIDTH_MEDIA_BREAKPOINT } from 'components/Tokens/constants'
import { useUniswapV3Staker } from 'hooks/useContract'
import { useEthPrices } from 'hooks/useEthPrices'
import React, { ReactNode, useEffect } from 'react'
import { fetchIncentives, fetchPools } from 'state/farm/actions'
import { useFarmIncentives, useFarmPools } from 'state/farm/hooks'
import { useAppDispatch } from 'state/hooks'
import { Status } from 'state/interfaces'
import { useCombinedActiveList } from 'state/lists/hooks'
import styled from 'styled-components/macro'

import { farms } from '../constants'
import { encodeIncentive } from '../utils'
import { HeaderRow, LoadedRow } from './FarmRow'

const GridContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: ${MAX_WIDTH_MEDIA_BREAKPOINT};
  margin-left: auto;
  margin-right: auto;
  border-radius: 12px;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.backgroundSwap};
  border: 1px solid ${({ theme }) => theme.backgroundSwapOutline};
  box-shadow: ${({ theme }) => theme.backgroundSwapShadow};
`

const FarmDataContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: 100%;
  width: 100%;
`

const NoFarmDisplay = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 60px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 16px;
  font-weight: 500;
  align-items: center;
  padding: 0 28px;
  gap: 8px;
`

function NoTokensState({ message }: { message: ReactNode }) {
  return (
    <GridContainer>
      <HeaderRow />
      <NoFarmDisplay>{message}</NoFarmDisplay>
    </GridContainer>
  )
}
interface Props {
  hideInactive?: boolean
}
const FarmTable = ({ hideInactive }: Props) => {
  const dispatch = useAppDispatch()
  const uniswapV3Staker = useUniswapV3Staker()
  const xdcPrice = useEthPrices()
  const list = useCombinedActiveList()

  const poolsState = useFarmPools()
  const incentivesState = useFarmIncentives()

  useEffect(() => {
    dispatch(fetchPools({ farms }))
  }, [])

  useEffect(() => {
    if (list && poolsState.status === Status.SUCCESS && uniswapV3Staker) {
      const ids = farms.map((farm) => {
        const incentiveKey = {
          rewardToken: farm.rewardToken,
          pool: farm.pool,
          startTime: farm.startTime,
          endTime: farm.endTime,
          vestingPeriod: farm.vestingPeriod,
          refundee: farm.refundee,
        }

        const encoded = encodeIncentive(incentiveKey)
        return keccak256(encoded)
      })
      dispatch(fetchIncentives({ list, poolsMap: poolsState.data, ids }))
    }
  }, [uniswapV3Staker, list, poolsState])

  return (
    <GridContainer>
      <HeaderRow />
      <FarmDataContainer>
        {farms
          // .filter((farm) => !hideInactive || farm.endTime * 1000 > Date.now())
          .map((farm, index) => {
            const incentive = incentivesState.data[farm.incentiveId]
            const pool = poolsState.data[farm.pool]
            return (
              <div
                key={`FarmTable-LoadedRow-${index}`}
                style={{ display: hideInactive && farm.endTime * 1000 < Date.now() ? 'none' : 'inherit' }}
              >
                <LoadedRow
                  rewardTokenPrice={pool ? pool.rewardTokenPrice : '0'}
                  farmListIndex={index}
                  farmListLength={farms.length}
                  farm={{
                    ...farm,
                    totalReward: incentive ? incentive.reward : null,
                    poolTvlInXdc: pool ? pool.totalValueLockedETH : '0',
                    stakedTvlInXdc: incentive ? incentive.stakedInXDC : null,
                  }}
                  xdcPrice={xdcPrice}
                />
              </div>
            )
          })}
      </FarmDataContainer>
    </GridContainer>
  )
}

export default FarmTable
